import { ApiService } from '@/apiConnection/ApiService';
import { Urls } from '@/apiConnection/urls';
import { CustomInputComponent } from '@/components/custom-input/custom-input.component';
import { CustomModalComponent } from '@/components/custom-modal/custom-modal.component';
import { CustomSelectComponent } from '@/components/custom-select/custom-select.component';
import { LoadingComponent } from '@/components/loading/loading.component';
import { CentToEurDisplayDirective } from '@/directives/cent-to-eur-display.directive';
import { Utils } from '@/utils/utils';
import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

/**
 * New cash closing (id = 0): shows the backend's preview of the open period and asks for the
 * counted cash. Existing closing (id != 0): read-only summary, closings are never edited.
 * Money from the API is in cents; the form holds euros and converts on save.
 */
@Component({
  selector: 'app-cash-closing-form',
  imports: [
    CustomModalComponent,
    ReactiveFormsModule,
    CustomInputComponent,
    CustomSelectComponent,
    LoadingComponent,
    CentToEurDisplayDirective,
    DatePipe,
  ],
  templateUrl: './cash-closing-form.component.html',
})
export class CashClosingFormComponent {
  @Input() id: number | string = 0;
  @Output() closeFunction = new EventEmitter<any>();
  @Output() acceptFunction = new EventEmitter<any>();

  public loading = false;
  public saving = false;

  /** Preview (new) or saved closing (view), amounts in cents. */
  public summary: any = null;
  public offices: any[] = [];

  /**
   * Optional closing dates (`YYYY-MM-DD`), shown as today by default. A date is only sent once the
   * user changes it; until then the backend uses its own default period (since the previous
   * closing, or today 00:00 for the first one, until now).
   */
  public periodStart = this.today();
  public periodEnd = this.today();
  private periodStartEdited = false;
  private periodEndEdited = false;
  public periodError: string | null = null;

  public saveForm: FormGroup = new FormGroup({
    office_id: new FormControl(null),
    opening_amount: new FormControl(0, [Validators.required, Validators.min(0)]),
    // Optional: without a count there's no difference, the drawer is assumed to hold the expected cash
    counted_cash: new FormControl(null, [Validators.min(0)]),
    cash_withdrawn: new FormControl(0, [Validators.min(0)]),
    notes: new FormControl(''),
  });

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) { }

  get isNew(): boolean {
    return this.id == 0;
  }

  ngOnInit(): void {
    if (this.isNew) {
      this.loadOffices();
      this.loadPreview();
      this.saveForm.get('office_id')?.valueChanges.subscribe(() => this.loadPreview());
    } else {
      this.getById(this.id);
    }
  }

  closeFormFunction() {
    this.closeFunction.emit();
  }

  // ── Live figures (cents) ─────────────────────────────────────────────────────

  private toCents(value: any): number {
    return Math.round((Number(value) || 0) * 100);
  }

  get expectedCash(): number {
    if (!this.isNew) return this.summary?.expected_cash ?? 0;
    return this.toCents(this.saveForm.value.opening_amount) + (this.summary?.cash_sales ?? 0);
  }

  get difference(): number {
    if (!this.isNew) return this.summary?.difference ?? 0;
    return this.toCents(this.saveForm.value.counted_cash) - this.expectedCash;
  }

  get countedEntered(): boolean {
    const counted = this.isNew ? this.saveForm.value.counted_cash : this.summary?.counted_cash;
    return counted !== null && counted !== undefined && counted !== '';
  }

  /** Counted cash minus withdrawal; falls back to the expected cash when the drawer wasn't counted. */
  get remainingCash(): number {
    if (!this.isNew) {
      return (this.summary?.counted_cash ?? this.summary?.expected_cash ?? 0) - (this.summary?.cash_withdrawn ?? 0);
    }
    const base = this.countedEntered ? this.toCents(this.saveForm.value.counted_cash) : this.expectedCash;
    return base - this.toCents(this.saveForm.value.cash_withdrawn);
  }

  // ── Period ───────────────────────────────────────────────────────────────────

  /** Today as `YYYY-MM-DD` in local time, for a date input. */
  private today(): string {
    const d = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  onPeriodStartChange(value: string) {
    this.periodStart = value;
    this.periodStartEdited = !!value;
    this.loadPreview();
  }

  onPeriodEndChange(value: string) {
    this.periodEnd = value;
    this.periodEndEdited = !!value;
    this.loadPreview();
  }

  resetPeriod() {
    this.periodStart = this.today();
    this.periodEnd = this.today();
    this.periodStartEdited = false;
    this.periodEndEdited = false;
    this.loadPreview();
  }

  get periodEdited(): boolean {
    return this.periodStartEdited || this.periodEndEdited;
  }

  /** office + edited period dates, shared by preview and save. */
  private periodParams(): Record<string, string> {
    const params: Record<string, string> = {};
    const officeId = this.saveForm.get('office_id')?.value;
    if (officeId) params['office_id'] = String(officeId);
    if (this.periodStartEdited) params['period_start'] = this.periodStart;
    if (this.periodEndEdited) params['period_end'] = this.periodEnd;
    return params;
  }

  print() {
    window.open(`/cash-closing/print/${this.id}`, '_blank');
  }

  // ── Data ─────────────────────────────────────────────────────────────────────

  loadOffices() {
    this.apiService.getSelectors(Urls.urls.selectors, { offices: {} }).subscribe({
      next: (data: any) => {
        this.offices = data.offices || [];
        this.cdr.detectChanges();
      }
    });
  }

  loadPreview() {
    this.loading = true;
    const params = '?' + new URLSearchParams(this.periodParams()).toString();

    this.apiService.getPetition(Urls.urls.cashClosingsPreview, params).subscribe({
      next: (data: any) => {
        this.summary = data;
        this.periodError = null;
        // Don't overwrite an opening amount the user already typed
        if (!this.saveForm.get('opening_amount')?.dirty) {
          this.saveForm.get('opening_amount')?.setValue((data.suggested_opening_amount ?? 0) / 100);
        }
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        // An invalid range (end before start, ...) is shown inline rather than as a toast
        this.summary = null;
        this.periodError = Utils.extractErrorMessage(error) || 'Error al calcular el cierre de caja';
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  getById(id: number | string) {
    this.loading = true;
    this.apiService.getById(Urls.urls.cashClosings, id).subscribe({
      next: (data: any) => {
        this.summary = data;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al obtener el cierre de caja');
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  saveEntity() {
    if (!this.summary || this.periodError) {
      Utils.notification.alert(this.periodError || 'Revisa el periodo del cierre');
      return;
    }
    if (!this.saveForm.valid) {
      this.saveForm.markAllAsTouched();
      Utils.notification.alert('Debe completar todos los campos obligatorios');
      return;
    }
    if (this.countedEntered && this.remainingCash < 0) {
      Utils.notification.alert('La retirada no puede ser mayor que el efectivo contado');
      return;
    }

    const value = this.saveForm.value;
    const payload = {
      ...this.periodParams(),
      office_id: value.office_id || null,
      opening_amount: this.toCents(value.opening_amount),
      counted_cash: this.countedEntered ? this.toCents(value.counted_cash) : null,
      cash_withdrawn: this.toCents(value.cash_withdrawn),
      notes: value.notes || null,
    };

    // Plain POST, not offline-queued: the totals are computed by the server at closing time
    this.saving = true;
    this.apiService.post(Urls.urls.cashClosings, payload).subscribe({
      next: (response: any) => {
        Utils.notification.success('Cierre de caja realizado');
        this.acceptFunction.emit(response);
        this.closeFormFunction();
      },
      error: (error: any) => {
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al guardar el cierre de caja');
        this.saving = false;
        this.cdr.detectChanges();
      },
    });
  }
}
